import { useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import css from "@styled-system/css";

import Box from "../Box/Box";
import Typography from "../Typography/Typography";
import { FeaturedProduct } from "../FeaturedProductPanel/FeaturedProductPanel";
import Step from "./Step";

import { ProductBlockProps, TimelineBarProps } from "./timeline.type";

const Product = styled.li`
  ${css({
    display: "flex",
    flexDirection: ["column", "column", "row"],
    justifyContent: "center",
  })}
`;

const DashedLineWrapper = styled.div`
  flex: 1;
  position: relative;
`;

const DashedLine = styled.div`
  ${css({
    display: "flex",
    borderTopWidth: ["2px ", "2px ", 0],
    borderLeftWidth: [0, 0, "2px "],
    borderStyle: "dashed",
    borderColor: "secondary-orange",
    borderRightWidth: 0,
    borderBottomWidth: 0,
    transition: "all 0.20s ease-in-out",
    position: "absolute",
    width: 0,
    height: 0,
    mx: [2, 2, 0],
    my: [0, 0, 2],
    "&.active": {
      height: "calc(100% - 16px)",
      width: "calc(100% - 16px)",
      opacity: "unset",
    },
  })}
`;

const DashedLineInactive = styled(DashedLine)`
  ${css({
    position: "absolute",
    borderTop: [`2px dashed #fff`, `2px dashed #fff`, 0],
    borderLeft: [0, 0, `2px dashed #fff`],
    opacity: 0.25,
    height: [0, 0, "calc(100% - 16px)"],
    width: ["calc(100% - 16px)", "calc(100% - 16px)", 0],
  })}
`;

const TimelineProduct = styled(FeaturedProduct)<{ css?: () => {} }>`
  ${(props) => props.css}
  ${css({
    minWidth: ["218px", "228px", "312px"],
    width: ["218px", "228px", "312px"],
    py: 4,
    px: [0, 0, 4],
    pr: [7, "60px", 4],
  })};
`;

const TimelineBar = ({
  index,
  productsNumber,
  onItemClick,
  isActive,
  backgroundColor,
  isStepActive,
}: TimelineBarProps) => (
  <Box
    display="flex"
    flexDirection={["row", "row", "column"]}
    alignItems="center"
    height={["auto", "auto", "100%"]}
    width="100%"
    arial-label="timeline"
    mt={[0, 0, 4]}
  >
    <Step
      stepNo={index + 1}
      isActive={isStepActive}
      onStepClick={onItemClick}
      backgroundColor={backgroundColor}
    />
    {index < productsNumber - 1 && (
      <DashedLineWrapper>
        <DashedLine className={isActive ? "active" : ""} />
        <DashedLineInactive />
      </DashedLineWrapper>
    )}
  </Box>
);

const ProductBlock = ({
  product,
  productsNumber,
  index,
  prevIndex,
  activeIndex,
  setActiveIndex,
  setPrevIndex,
  backgroundColor,
  descriptionColor,
  htmlTag = "li",
}: ProductBlockProps) => {
  const [stepActive, setStepActive] = useState(false);
  const [timelineBarActive, setTimelineBarActive] = useState(false);
  const timelineBarAnimationTimeout = 200;
  const stepAnimationTimeout = 75;

  const handleForwardNavigation = useCallback(() => {
    const timeout = (index - prevIndex) * timelineBarAnimationTimeout;

    if (index > activeIndex) {
      return;
    }

    if (index === activeIndex) {
      setTimeout(() => setStepActive(true), timeout);
      return;
    }

    if (activeIndex - prevIndex === 1) {
      setTimelineBarActive(true);
      setStepActive(true);

      return;
    }

    if (activeIndex - prevIndex > 1) {
      setTimeout(
        () => setTimelineBarActive(true),
        timeout + stepAnimationTimeout
      );

      setTimeout(() => setStepActive(true), timeout);

      return;
    }
  }, [activeIndex, index, prevIndex]);

  const handleBackwardsNavigation = useCallback(() => {
    if (index < activeIndex) {
      return;
    }

    if (index !== activeIndex) {
      setTimeout(
        () => setStepActive(false),
        (prevIndex - index) * timelineBarAnimationTimeout
      );
    }

    if (prevIndex - index === 1) {
      setTimelineBarActive(false);

      return;
    }

    if (activeIndex === 0) {
      setTimeout(
        () => setTimelineBarActive(false),
        (prevIndex - index - 1) * timelineBarAnimationTimeout +
          stepAnimationTimeout
      );

      return;
    }

    if (activeIndex > 0) {
      setTimeout(
        () => setTimelineBarActive(false),
        (prevIndex - activeIndex - index) * timelineBarAnimationTimeout +
          stepAnimationTimeout
      );

      return;
    }
  }, [activeIndex, index, prevIndex]);

  useEffect(() => {
    if (activeIndex === 0 && prevIndex === 0 && index === 0) {
      setStepActive(true);
      return;
    }

    if (activeIndex > prevIndex) {
      handleForwardNavigation();
      return;
    }

    handleBackwardsNavigation();
  }, [
    activeIndex,
    prevIndex,
    handleForwardNavigation,
    handleBackwardsNavigation,
    index,
    stepActive,
    timelineBarActive,
  ]);

  const eventTitle =
    product.title && product.title.length > 250
      ? product.title.substr(0, 250)
      : product.title;

  const onItemClick = () => {
    setActiveIndex((prevIndex) => {
      setPrevIndex(prevIndex);
      return index;
    });
  };

  const bottomMargin = index !== productsNumber - 1 ? [0, 0, 8] : "1px";

  return (
    <Product
      role="tab"
      aria-selected={activeIndex === index ? true : false}
      aria-controls={product.id}
      as={htmlTag}
    >
      <TimelineBar
        index={index}
        productsNumber={productsNumber}
        onItemClick={onItemClick}
        isActive={timelineBarActive}
        backgroundColor={backgroundColor}
        isStepActive={stepActive}
      />
      <TimelineProduct
        key={product.id}
        className={index <= activeIndex ? "active" : ""}
        onClick={onItemClick}
        as="div"
        data-en="click"
        data-ec="panel"
        data-el={eventTitle || ""}
        data-ea={product.analytics?.eventAction}
        data-es={product.analytics?.eventSection}
        data-et={product.analytics?.eventType}
        data-ev={product.analytics?.eventValue}
        data-testid="container-item"
        css={css({ mb: bottomMargin })}
      >
        <Typography variant="h5" color="primary-white" m={0} mb={3}>
          {product.title}
        </Typography>
        <Typography variant="body" color={descriptionColor} m={0}>
          {product.description}
        </Typography>
      </TimelineProduct>
    </Product>
  );
};

export default ProductBlock;
